import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { EphemeralTokenService } from './ephemeral-token.service';
import { EPHEMERAL_TOKEN_CONFIG, requiresEphemeralToken, getSecurityEndpointUrl } from './ephemeral-token.config';
import { NotificationService } from 'src/app/services/notification.service';

/**
 * Interceptor HTTP para tokens efímeros
 * 
 * FUNCIONALIDAD:
 * 1. Detecta requests que requieren token efímero
 * 2. Obtiene token automáticamente
 * 3. Añade header Authorization: Bearer <token>
 * 4. Redirige request al servidor de seguridad
 * 5. Maneja errores 401 con reintento automático (1 vez)
 * 6. Limpia token después de usar (un solo uso)
 * 
 * ⚠️ IMPORTANTE: Este interceptor NO está registrado en app.module.ts
 * Para activarlo, agregar en providers:
 * 
 * {
 *   provide: HTTP_INTERCEPTORS,
 *   useClass: EphemeralTokenInterceptor,
 *   multi: true
 * }
 * 
 * NOTA: Debe ir ANTES de TokenInterceptor en el array de providers
 */
@Injectable()
export class EphemeralTokenInterceptor implements HttpInterceptor {
  /**
   * Marcador para evitar loops infinitos de reintento
   */
  private retryMap = new WeakMap<HttpRequest<any>, number>();

  constructor(
    private ephemeralTokenService: EphemeralTokenService,
    private notificationService: NotificationService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Solo interceptar requests que requieren token efímero
    if (!requiresEphemeralToken(req.url)) {
      return next.handle(req);
    }

    // Obtener token y modificar request
    return this.ephemeralTokenService.getToken().pipe(
      switchMap(token => {
        
        // Clonar request SOLO añadiendo el token, SIN cambiar la URL
        // IMPORTANTE: setHeaders PRESERVA los headers existentes y añade los nuevos
        const clonedReq = req.clone({
          setHeaders: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Ejecutar request y manejar errores
        return next.handle(clonedReq).pipe(
          catchError((error: HttpErrorResponse) => {
            // Manejar errores específicos de autenticación/autorización
            this.handleAuthErrors(error);
            
            // Intentar reintentar si es error 401
            return this.handleError(error, req, next);
          })
        );
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Convierte URL del backend normal a URL del servidor de seguridad
   * 
   * @param originalUrl URL original
   * @returns URL del servidor de seguridad
   */
  private getSecurityUrl(originalUrl: string): string {
    // Si ya es una URL completa del servidor de seguridad, retornarla
    if (originalUrl.includes(EPHEMERAL_TOKEN_CONFIG.securityApiUrl)) {
      return originalUrl;
    }

    // Extraer el path del endpoint
    // Ej: https://dev.hecbill.hecticus.com/suscription -> /suscription
    let endpoint = originalUrl;
    
    // Si es una URL completa, extraer solo el path
    try {
      const url = new URL(originalUrl);
      endpoint = url.pathname + url.search;
    } catch {
      // Si falla, asumir que es solo el path
    }

    // NO modificar el endpoint, mantener la URL original
    return getSecurityEndpointUrl(endpoint);
  }

  /**
   * Maneja errores HTTP, especialmente 401 (token expirado)
   * 
   * @param error Error HTTP
   * @param originalReq Request original
   * @param next Handler
   * @returns Observable con resultado o error
   */
  private handleError(
    error: HttpErrorResponse,
    originalReq: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Solo manejar errores 401 (Unauthorized)
    if (error.status !== 401) {
      return throwError(() => error);
    }

    // Obtener contador de reintentos
    const retryCount = this.retryMap.get(originalReq) || 0;

    // Verificar si ya se alcanzó el máximo de reintentos
    if (retryCount >= EPHEMERAL_TOKEN_CONFIG.maxRetries) {
      this.retryMap.delete(originalReq);
      return throwError(() => error);
    }

    // Incrementar contador de reintentos
    this.retryMap.set(originalReq, retryCount + 1);

    // Limpiar token actual y obtener uno nuevo
    this.ephemeralTokenService.clearToken();

    return this.ephemeralTokenService.getToken().pipe(
      switchMap(newToken => {
        const securityUrl = this.getSecurityUrl(originalReq.url);

        const retryReq = originalReq.clone({
          url: securityUrl,
          setHeaders: {
            'Authorization': `Bearer ${newToken}`
          }
        });

        return next.handle(retryReq).pipe(
          catchError(retryError => {
            this.retryMap.delete(originalReq);
            return throwError(() => retryError);
          })
        );
      }),
      catchError(tokenError => {
        this.retryMap.delete(originalReq);
        return throwError(() => tokenError);
      })
    );
  }

  /**
   * Maneja errores de autenticación y autorización (401/403)
   * Muestra mensajes de error al usuario
   * 
   * @param error HttpErrorResponse
   */
  private handleAuthErrors(error: HttpErrorResponse): void {
    let errorMessage: string | null = null;

    switch (error.status) {
      case 401:
        errorMessage = 'La sesión de seguridad ha expirado o es inválida. Por favor, intente nuevamente.';
        this.ephemeralTokenService.clearToken();
        break;

      case 403:
        errorMessage = 'No tiene permisos para realizar esta operación. Por favor, contacte con soporte.';
        break;

      default:
        return;
    }

    if (errorMessage) {
      this.notificationService.showError(errorMessage);
    }
  }
}
